import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Plus, Loader2, Trash2, Copy, Check, MessageSquare, Bot, User, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { streamChat } from "@/lib/gateway";

function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="group relative my-2 overflow-hidden rounded-lg border border-border bg-[#070B19]">
      <div className="flex items-center justify-between border-b border-border/60 px-3 py-1.5">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{lang || "code"}</span>
        <button onClick={copy} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-gold-400" data-testid="msg-copy-code">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 font-mono text-xs leading-relaxed text-slate-200"><code>{code}</code></pre>
    </div>
  );
}

function MessageContent({ text, mono }) {
  if (!text) return null;
  if (!text.includes("```")) {
    return <div className={`whitespace-pre-wrap ${mono ? "font-mono text-[13px]" : "text-sm"} leading-relaxed`}>{text}</div>;
  }
  const parts = text.split(/```/);
  return (
    <div>
      {parts.map((seg, i) => {
        if (i % 2 === 1) {
          const nl = seg.indexOf("\n");
          const lang = nl > 0 && nl < 20 ? seg.slice(0, nl).trim() : "";
          const code = nl > 0 && lang ? seg.slice(nl + 1) : seg;
          return <CodeBlock key={i} code={code.replace(/\n$/, "")} lang={lang} />;
        }
        return seg ? <div key={i} className="whitespace-pre-wrap text-sm leading-relaxed">{seg}</div> : null;
      })}
    </div>
  );
}

export default function ChatStudio({ kind = "chat", system, onCredits, placeholder, mono = false, defaultModel = "crerea-pro" }) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(defaultModel);
  const [convos, setConvos] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [latency, setLatency] = useState(null);
  const scrollRef = useRef(null);

  const loadConvos = useCallback(() => {
    api.get(`/conversations?kind=${kind}`).then((r) => setConvos(r.data)).catch(() => {});
  }, [kind]);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data.filter((m) => m.type === "text"))).catch(() => {});
    loadConvos();
  }, [loadConvos]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, streaming]);

  const current = models.find((m) => m.id === modelId);
  const cost = current?.cost ?? 1;

  const newChat = () => { setConvId(null); setMessages([]); setError(""); setLatency(null); };

  const openConvo = async (id) => {
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setConvId(data.id); setMessages(data.messages || []); setModelId(data.model || defaultModel); setError(""); setLatency(null);
    } catch { toast.error("Impossible de charger la conversation"); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/conversations/${id}`);
    if (id === convId) newChat();
    loadConvos();
  };

  const persist = async (msgs) => {
    try {
      if (convId) {
        await api.put(`/conversations/${convId}`, { messages: msgs, model: modelId });
      } else {
        const title = (msgs[0]?.content || "Conversation").slice(0, 42);
        const { data } = await api.post("/conversations", { kind, title, model: modelId, messages: msgs });
        setConvId(data.id);
      }
      loadConvos();
    } catch { /* non-blocking */ }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setError(""); setInput("");
    const base = [...messages, { role: "user", content: text }];
    setMessages([...base, { role: "assistant", content: "" }]);
    setStreaming(true);
    const t0 = performance.now();
    let acc = "";
    try {
      await streamChat({
        model: modelId, system, messages: base,
        onDelta: (d) => { acc += d; setMessages([...base, { role: "assistant", content: acc }]); },
      });
      setLatency(Math.round(performance.now() - t0));
      const final = [...base, { role: "assistant", content: acc }];
      setMessages(final);
      onCredits?.();
      persist(final);
    } catch (e) {
      const msg = e.status === 402 ? "Crédits insuffisants — rechargez votre solde." : (e.message || "Échec de la génération");
      setError(msg); toast.error(msg, { duration: 7000 });
      setMessages(base);
    }
    setStreaming(false);
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
      {/* History */}
      <div className="rounded-2xl border border-border bg-card p-3" data-testid="chat-history">
        <Button onClick={newChat} data-testid="new-chat-btn" className="mb-3 w-full rounded-full bg-gold-500 text-black hover:bg-gold-400"><Plus className="mr-1 h-4 w-4" />Nouvelle</Button>
        <div className="space-y-1 max-h-[520px] overflow-y-auto">
          {convos.length === 0 && <p className="px-2 py-3 text-xs text-muted-foreground">Aucune conversation.</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => openConvo(c.id)} data-testid={`convo-${c.id}`}
              className={`group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${convId === c.id ? "bg-gold-500/15 text-gold-400" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">{c.title}</span>
              <Trash2 onClick={(e) => del(c.id, e)} className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      </div>

      {/* Chat panel */}
      <div className="flex min-h-[560px] flex-col rounded-2xl border border-gold-500/25 bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          {models.map((m) => (
            <button key={m.id} onClick={() => setModelId(m.id)} data-testid={`model-select-${m.id}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${modelId === m.id ? "border-gold-500 bg-gold-500/10 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {m.label} <span className="font-mono opacity-70">{m.cost}cr</span>
            </button>
          ))}
          <span className="ml-auto flex items-center gap-2 font-mono text-xs text-muted-foreground">
            {latency != null && <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{latency}ms</span>}
            {streaming && <span className="flex items-center gap-1 text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />streaming</span>}
          </span>
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-messages" style={{ maxHeight: "460px" }}>
          {messages.length === 0 && !error && (
            <div className="grid h-full place-items-center text-center text-sm text-muted-foreground">
              <div><Bot className="mx-auto mb-2 h-8 w-8 text-gold-400/60" />Démarrez la conversation. Le fil est conservé automatiquement.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-gold-500/15 text-gold-400"><Bot className="h-4 w-4" /></div>}
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-gold-500/15 text-foreground" : "bg-secondary/50"}`} data-testid={`msg-${m.role}-${i}`}>
                <MessageContent text={m.content} mono={mono} />
                {m.role === "assistant" && !m.content && streaming && <span className="inline-block h-4 w-1.5 animate-pulse bg-gold-400" />}
              </div>
              {m.role === "user" && <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground"><User className="h-4 w-4" /></div>}
            </motion.div>
          ))}
          {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="chat-error">{error}</div>}
        </div>

        <div className="border-t border-border p-3">
          <div className="flex items-end gap-2">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1} data-testid="chat-input"
              placeholder={placeholder || "Écrivez votre message… (Entrée pour envoyer)"}
              className="max-h-32 flex-1 resize-none rounded-xl border border-border bg-secondary/40 px-4 py-2.5 text-sm outline-none focus:border-gold-500/60" />
            <Button onClick={send} disabled={streaming || !input.trim()} data-testid="chat-send-btn" className="rounded-full bg-gold-500 text-black hover:bg-gold-400">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">Coût : <span className="font-mono text-gold-400">{cost} cr.</span> / message · le fil est envoyé au modèle pour le contexte</p>
        </div>
      </div>
    </div>
  );
}
